/**
 * Product Hero Gallery composition root.
 * Composes gallery behaviors and owns the public runtime bootstrap.
 */

(() => {
  const PRODUCT_HERO_GALLERY_RUNTIME = 'MarineMaxProductHeroGallery';
  const PRODUCT_HERO_GALLERY_SELECTOR =
    '.product__hero-banner[data-section-id]';
  const existingRuntime = window[PRODUCT_HERO_GALLERY_RUNTIME];

  if (existingRuntime?.initialize) {
    existingRuntime.initialize(document);
    return;
  }

  const productHeroGalleryHistory =
    window.__MarineMaxProductHeroGalleryHistory;
  const productHeroGalleryCarousel =
    window.__MarineMaxProductHeroGalleryCarousel;
  const productHeroGalleryContentModals =
    window.__MarineMaxProductHeroGalleryContentModals;
  const productHeroGalleryMediaHub =
    window.__MarineMaxProductHeroGalleryMediaHub;
  const productHeroGalleryLightboxCore =
    window.__MarineMaxProductHeroGalleryLightboxCore;
  const productHeroGalleryLightboxGestures =
    window.__MarineMaxProductHeroGalleryLightboxGestures;
  const productHeroGalleryLifecycle =
    window.__MarineMaxProductHeroGalleryLifecycle;

  const requiredModules = [
    { dependency: productHeroGalleryHistory, name: 'history' },
    { dependency: productHeroGalleryCarousel, name: 'carousel' },
    { dependency: productHeroGalleryContentModals, name: 'content modals' },
    { dependency: productHeroGalleryMediaHub, name: 'Media Hub' },
    { dependency: productHeroGalleryLightboxCore, name: 'Lightbox core' },
    {
      dependency: productHeroGalleryLightboxGestures,
      name: 'Lightbox gestures',
    },
    { dependency: productHeroGalleryLifecycle, name: 'lifecycle' },
  ];
  const missingModule = requiredModules.find(
    ({ dependency }) => !dependency?.install,
  );

  if (missingModule) {
    console.error(
      `Product Hero Gallery ${missingModule.name} module must load before the gallery entrypoint.`,
    );
    return;
  }

  class ProductHeroGallery {
    constructor(sectionId, heroBanner = null) {
      this.sectionId = sectionId;
      this.historyStateKey = 'productHeroGallery';
      this.legacyHistoryHashPrefix = '#phg=';
      this.heroBanner =
        heroBanner ||
        document.querySelector(
          `.product__hero-banner[data-section-id="${sectionId}"]`,
        );
      this.instanceRegistry =
        window.__productHeroGalleryInstances ||
        (window.__productHeroGalleryInstances = new Map());

      if (!this.heroBanner) return;
      if (this.heroBanner.__productHeroGalleryInstance) {
        return this.heroBanner.__productHeroGalleryInstance;
      }

      const existingInstance = this.instanceRegistry.get(sectionId);
      if (existingInstance && existingInstance !== this) {
        if (existingInstance.heroBanner === this.heroBanner) {
          this.heroBanner.__productHeroGalleryInstance = existingInstance;
          return existingInstance;
        }

        existingInstance.destroy?.();
      }

      this.eventAbortController = new AbortController();
      this.initializeHeroCarouselState();
      this.initializeContentModalsState();
      this.initializeLightboxGesturesState();
      this.initializeLightboxCoreState();
      this.initializeMediaHubState();
      this.pendingHistoryBackFallback = null;
      this.heroBanner.__productHeroGalleryInstance = this;
      this.instanceRegistry.set(sectionId, this);

      this.init();
    }
  }

  productHeroGalleryHistory.install(ProductHeroGallery);
  productHeroGalleryCarousel.install(ProductHeroGallery);
  productHeroGalleryContentModals.install(ProductHeroGallery);
  productHeroGalleryMediaHub.install(ProductHeroGallery);
  productHeroGalleryLightboxCore.install(ProductHeroGallery);
  productHeroGalleryLightboxGestures.install(ProductHeroGallery);
  productHeroGalleryLifecycle.install(ProductHeroGallery);

  function collectProductHeroBanners(root = document) {
    if (!root) return [];

    const banners = [];
    if (
      typeof root.matches === 'function' &&
      root.matches(PRODUCT_HERO_GALLERY_SELECTOR)
    ) {
      banners.push(root);
    }
    if (typeof root.querySelectorAll === 'function') {
      banners.push(...root.querySelectorAll(PRODUCT_HERO_GALLERY_SELECTOR));
    }

    return [...new Set(banners)];
  }

  function initProductHeroGalleries(root = document) {
    const instances = [];

    collectProductHeroBanners(root).forEach((banner) => {
      const sectionId = banner.dataset.sectionId;
      const existingInstance = banner.__productHeroGalleryInstance;
      if (existingInstance) {
        existingInstance.refreshAfterPageRestore();
        instances.push(existingInstance);
        return;
      }

      const instance = new ProductHeroGallery(sectionId, banner);
      if (instance && typeof instance.refreshAfterPageRestore === 'function') {
        instances.push(instance);
      }
    });

    return instances;
  }

  function recoverProductHeroGalleries() {
    return initProductHeroGalleries(document);
  }

  function destroyProductHeroGalleries(root) {
    collectProductHeroBanners(root).forEach((banner) => {
      banner.__productHeroGalleryInstance?.destroy?.();
    });
  }

  function scheduleProductHeroGalleryRecovery() {
    window.requestAnimationFrame(recoverProductHeroGalleries);
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      scheduleProductHeroGalleryRecovery();
    }
  }

  function handleSectionLoad(event) {
    initProductHeroGalleries(event.target);
  }

  function handleSectionUnload(event) {
    destroyProductHeroGalleries(event.target);
  }

  const productHeroGalleryRuntime = {
    ProductHeroGallery,
    destroy: destroyProductHeroGalleries,
    initialize: initProductHeroGalleries,
    recover: recoverProductHeroGalleries,
  };

  window[PRODUCT_HERO_GALLERY_RUNTIME] = productHeroGalleryRuntime;
  window.ProductHeroGallery = ProductHeroGallery;
  window.initProductHeroGalleries = initProductHeroGalleries;
  window.recoverProductHeroGalleries = recoverProductHeroGalleries;

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      recoverProductHeroGalleries,
      { once: true },
    );
  } else {
    recoverProductHeroGalleries();
  }

  window.addEventListener('load', recoverProductHeroGalleries);
  window.addEventListener('pageshow', scheduleProductHeroGalleryRecovery);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
})();
